import { useMemo, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { ToastTypes } from "../../../contexts/toastr.context";
import { useToast } from "../../../hooks/use-toast.hook";
import { PrettiFy } from "../../../types/course/course.type";
import { MySubscription } from "../../../types/subscription/my-subscription-plan";
import { Button } from "../../../_components";
import { NewActionModal } from "../../../_components/action2-modal.component";
import LoginRequiredViewComponent from "../../../_components/login-required-view.component";
import Label from "../../../_components/typography/label.typography";
import { cancelMySubscription } from "../../../_store/subscription/my-subscription/my-subscription.action";
import { Fold } from "../../../_utils/extensions/typescript-utils";
import { SubscriptionUtils } from "../../../_utils/subscription/subscription-util";
import { CurrencySymbol } from "../../user-management-settings/subscriptions/component/subscription_tile.component";
import { useAppService } from "../../../hooks/use-app-service";

export const MEMBERSHIPS_PAGE_ROUTE = "/settings/memberships";

interface MembershipProps {
  mySubscription: MySubscription;
  user: any;
  community: any;
  cancelMySubscription: Function;
}

/**
 * Displays user's current membership plan
 */
const MembershipComponent = ({
  community,
  mySubscription,
  user,
  cancelMySubscription = () => {},
}: MembershipProps) => {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [visibleCancel, setVisibleCancel] = useState(false);

  const { analyticsService } = useAppService();

  function cancelSubscription() {
    setIsLoading(true);
    cancelMySubscription({
      onSuccess: () => {
        setIsLoading(false);
        setVisibleCancel(false);
        toast.addToast("Subscription cancelled successfully", "");
      },
      onError: (err: any) => {
        console.log(err);
        setIsLoading(false);
        toast.addToast("Failed to cancel subscription", "", ToastTypes.danger);
      },
    });
  }

  if (!user) {
    return <LoginRequiredViewComponent />;
  }
  return (
    <div className="MembershipComponent pensil-card theme-bg-surface flex flex-col rounded">
      <div className="  theme-bg-surface shadow p-4">
        <Label variant="t1">Current Membership</Label>
      </div>
      <Fold
        value={mySubscription?.subscriptionPlan}
        ifPresent={function (value: unknown): JSX.Element {
          return (
            <div className="rounded pensil-card theme-bg-surface shadow mt-2 p-4">
              <Plan subscription={mySubscription} />

              <div className="flex justify-end flex-row gap-2 mt-3 pt-8">
                <Button
                  label="Cancel Plan"
                  large
                  isLoading={isLoading}
                  btnColor="red"
                  onClick={() => {
                    analyticsService.track("cancel-plan",{
                      cancelledPlan: mySubscription?.subscriptionPlan
                    })
                    setVisibleCancel(true);
                  }}
                />
              </div>
            </div>
          );
        }}
        ifAbsent={function (): JSX.Element {
          return (
            <div className="rounded pensil-card theme-bg-surface shadow mt-2 p-4">
              <div className="flex flex-col gap-4">
                <Label variant="t2" translate>
                  You are not subscribed to any membership plan
                </Label>
              </div>
            </div>
          );
        }}
      />
      <NewActionModal
        title={"Are you sure you want to cancel your subscription?"}
        active={visibleCancel}
        setActive={setVisibleCancel}
        btnColor="red"
        onCancelSubmit={() => {
          setVisibleCancel(false);
        }}
        onSubmit={() => {
          cancelSubscription();
        }}
        isLoading={isLoading}
        width={400}
        labelCancel="No, keep my subscription"
        labelSubmit="Cancel my subscription"
        subTitle="You will lose access to all the premium features of the subscription plan."
      />
    </div>
  );
};

function Plan({ subscription }: { subscription?: PrettiFy<MySubscription> }) {
  if (!subscription || !subscription.subscriptionPlan) {
    return <></>;
  }

  const plan = subscription.subscriptionPlan;
  const amount = SubscriptionUtils.calculateSubscriptionTotal(plan);

  const currency =
    CurrencySymbol[plan.currency as keyof typeof CurrencySymbol] ||
    plan.currency;
  const isRecurring = plan.isRecurring;
  const purchasedDate = plan.createdAt;
  const interval = plan.recurringInterval;

  // Find the subscription renewal date
  var renewalDate = new Date(purchasedDate);
  if (isRecurring && interval) {
    renewalDate.setMonth(renewalDate.getMonth() + interval);
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const locale = useMemo(() => getLang(), []) ?? "en-US";

  function getLang() {
    if (navigator.languages !== undefined) return navigator.languages[0];
    return navigator.language;
  }

  return (
    <div
      className={
        "border theme-border-primary rounded px-4 py-3 theme-bg-default"
      }>
      <div className="flex">
        <div className="flex-grow ml-2">
          <div className="flex place-content-between">
            <Label variant="t1">{plan.name}</Label>
            <div>
              {amount ? (
                <div className="flex flex-col ">
                  <div className="whitespace-nowrap theme-text-heading-1">
                    <Label>
                      {currency} {amount}
                    </Label>
                    <Label>
                      {(plan?.recurringInterval ?? 0) > 0 &&
                        `per ${plan?.recurringInterval} month`}
                    </Label>
                  </div>

                  {subscription.subscriptionPlan.isRecurring && (
                    <>
                      Renew on{" "}
                      {renewalDate.toLocaleDateString(locale, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </>
                  )}
                </div>
              ) : (
                <Label translate variant="t1">
                  Free
                </Label>
              )}
            </div>
          </div>
          <div className="flex flex-row justify-between">
            {plan.description && (
              <div
                dangerouslySetInnerHTML={{ __html: plan.description }}
                className="NL2BR headings mt-2"></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const MembershipsPage = connect(
  (s: any) => ({
    community: s.community,
    user: s.auth,
    subscriptions: s.subscriptionsPlan,
    mySubscription: s.mySubscription,
  }),
  (d) =>
    bindActionCreators(
      {
        cancelMySubscription: cancelMySubscription,
      },
      d
    )
)(MembershipComponent);
export default MembershipsPage;
